import React from 'react';
import type { ScoringStats, TeamStats } from '../../../types';

interface TeamMember {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string;
}

interface Props {
  stats: ScoringStats | null;
  teamStats: TeamStats | null;
  leaderboard: Array<{ userId: string; name: string; avgScore: number; videoCount: number; avgGrade: string }>;
  teamMembers: TeamMember[];
  selectedUserId: string;
  loading: boolean;
  isAdmin: boolean;
  onSelectUser: (userId: string) => void;
}

const GRADE_COLORS: Record<string, string> = {
  A: 'bg-secondary',
  B: 'bg-primary',
  C: 'bg-tertiary',
  D: 'bg-orange-400',
  F: 'bg-error',
};

const GRADE_LABELS: Record<string, string> = {
  A: 'Elite',
  B: 'Proficient',
  C: 'Adequate',
  D: 'Weak',
  F: 'Failing',
};

const SIGNAL_LABELS: Record<string, string> = {
  zoneCoverage: 'Zone Coverage',
  faceOnCamera: 'Face On Camera',
  lighting: 'Lighting Quality',
  stability: 'Camera Stability',
  duration: 'Video Duration',
  speechPace: 'Speech Clarity',
  energy: 'Energy / Enthusiasm',
  buyerName: 'Buyer Name',
  featureCallout: 'Feature Callout',
  cta: 'Call to Action',
};

const SIGNAL_WEIGHTS: Record<string, string> = {
  zoneCoverage: '20%', faceOnCamera: '10%', lighting: '10%',
  stability: '5%', duration: '5%', speechPace: '10%',
  energy: '15%', buyerName: '10%', featureCallout: '5%', cta: '10%',
};

const getMemberName = (m: TeamMember) =>
  [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;

const TeamPerformanceTab: React.FC<Props> = ({
  stats,
  teamStats,
  leaderboard,
  teamMembers,
  selectedUserId,
  loading,
  isAdmin,
  onSelectUser,
}) => {
  const selectedMemberName = selectedUserId
    ? getMemberName(
        teamMembers.find((m) => m.id === selectedUserId) ?? {
          id: '',
          firstName: null,
          lastName: null,
          email: '',
          role: '',
        },
      )
    : 'All Team Members';

  const personalFlagPct =
    stats && stats.scoredCount > 0
      ? Math.min(100, ((stats.flaggedVideoCount ?? 0) / stats.scoredCount) * 100)
      : 0;
  const teamFlagPct =
    teamStats && teamStats.totalVideos > 0
      ? Math.min(100, ((teamStats.flaggedVideoCount ?? 0) / teamStats.totalVideos) * 100)
      : 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <div className="w-10 h-10 border-4 border-primary/20 border-t-primary animate-spin rounded-full shadow-lg shadow-primary/20" />
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 animate-pulse">
          Analyzing Performance Data...
        </span>
      </div>
    );
  }

  return (
    <>
      {isAdmin && teamMembers.length > 0 && (
        <div className="flex justify-end mb-6">
          <div className="flex flex-col gap-1.5 shrink-0">
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600 ml-1">
              Team Member
            </label>
            <select
              value={selectedUserId}
              onChange={(e) => onSelectUser(e.target.value)}
              className="h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer min-w-[200px]"
            >
              <option value="">All Team Members</option>
              {teamMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {getMemberName(m)}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Top Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {[
          {
            label: 'Average Score',
            value: stats?.averageScore != null ? stats.averageScore.toFixed(1) : '--',
            suffix: '/100',
            icon: 'auto_graph',
            color: 'text-primary',
          },
          {
            label: isAdmin && !selectedUserId ? 'Team Peak' : 'Personal Peak',
            value: stats?.highestScore ?? '--',
            suffix: 'best vqs',
            icon: 'workspace_premium',
            color: 'text-amber-500',
          },
          {
            label: 'Total Assessments',
            value: stats?.totalVideos ?? 0,
            suffix: 'videos',
            icon: 'videocam',
            color: 'text-secondary',
          },
          {
            label: 'Compliance Flags',
            value: stats?.flaggedVideoCount ?? '--',
            suffix: 'flagged',
            icon: 'verified_user',
            color: 'text-tertiary',
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-3 mb-4">
              <div
                className={`w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center ${stat.color} shadow-sm border border-slate-100 dark:border-slate-700`}
              >
                <span className="material-symbols-outlined text-[24px]">{stat.icon}</span>
              </div>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">
                {stat.label}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                {stat.value}
              </span>
              {stat.suffix && (
                <span className="text-xs font-medium text-slate-400 dark:text-slate-600">
                  {stat.suffix}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Grade Distribution */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary">
              <span className="material-symbols-outlined text-[24px]">pie_chart</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
              Grade Distribution
            </h2>
          </div>
          {stats?.gradeDistribution &&
          Object.values(stats.gradeDistribution).some((v) => v > 0) ? (
            <div className="space-y-5">
              {(['A', 'B', 'C', 'D', 'F'] as const).map((grade) => {
                const count = stats.gradeDistribution[grade] ?? 0;
                const total = Object.values(stats.gradeDistribution).reduce((s, v) => s + v, 0);
                const pct = total > 0 ? (count / total) * 100 : 0;
                return (
                  <div key={grade} className="flex items-center gap-4">
                    <div className="w-10 text-center font-bold text-base text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-800 py-1 rounded-lg border border-slate-100 dark:border-slate-700 shrink-0">
                      {grade}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          {GRADE_LABELS[grade]}
                        </span>
                        <span className="text-[10px] font-bold text-slate-400">{pct.toFixed(0)}%</span>
                      </div>
                      <div className="h-2.5 bg-slate-50 dark:bg-slate-800 rounded-full overflow-hidden border border-slate-100 dark:border-slate-700/50 p-[1px]">
                        <div
                          className={`h-full ${GRADE_COLORS[grade]} rounded-full transition-all duration-1000 ease-out`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-xs font-bold text-slate-900 dark:text-white w-6 text-right shrink-0">
                      {count}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 border border-slate-100 dark:border-slate-800 rounded-xl flex flex-col items-center">
              <span className="material-symbols-outlined text-slate-200 dark:text-slate-800 text-4xl mb-2">
                bar_chart_4_bars
              </span>
              <p className="text-slate-400 dark:text-slate-600 text-sm font-medium">
                No scored videos yet
              </p>
            </div>
          )}
        </div>

        {/* Leaderboard / Team Benchmarking */}
        {isAdmin && !selectedUserId ? (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/20 flex items-center justify-center text-orange-500">
                  <span className="material-symbols-outlined text-[24px]">workspace_premium</span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
                  Top Performers
                </h2>
              </div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                By Avg Score
              </span>
            </div>
            {leaderboard.length === 0 ? (
              <div className="py-12 border border-slate-100 dark:border-slate-800 rounded-xl flex flex-col items-center">
                <span className="material-symbols-outlined text-slate-200 dark:text-slate-800 text-4xl mb-2">
                  military_tech
                </span>
                <p className="text-slate-400 dark:text-slate-600 text-sm font-medium">No data yet</p>
              </div>
            ) : (
              <div className="space-y-1">
                {leaderboard.slice(0, 10).map((rep, i) => (
                  <div
                    key={rep.userId}
                    className="group flex items-center gap-4 py-3 border-b border-slate-50 dark:border-slate-800 last:border-0 cursor-pointer hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-all px-4 -mx-4 rounded-xl"
                    onClick={() => onSelectUser(rep.userId)}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs border border-transparent group-hover:scale-110 transition-transform shadow-sm ${
                        i === 0
                          ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                          : i === 1
                            ? 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                            : i === 2
                              ? 'bg-orange-100 text-orange-600 dark:bg-orange-900/30'
                              : 'bg-slate-50 text-slate-400 dark:bg-slate-900'
                      }`}
                    >
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-slate-900 dark:text-white truncate group-hover:text-primary transition-colors">
                        {rep.name}
                      </div>
                      <div className="text-[10px] text-slate-400 flex items-center gap-2 mt-0.5">
                        <span className="font-bold">{rep.videoCount} Videos</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200 dark:bg-slate-800" />
                        <span>Sales Representative</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-black text-primary tracking-tight">
                        {rep.avgScore.toFixed(0)}
                      </div>
                      <div className="text-[9px] font-black uppercase text-slate-300 dark:text-slate-700 tracking-widest">
                        Avg Score
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center text-blue-500">
                  <span className="material-symbols-outlined text-[24px]">query_stats</span>
                </div>
                <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
                  Team Benchmarking
                </h2>
              </div>
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">
                {isAdmin ? `${selectedMemberName} vs. Team` : 'Personal vs. Team'}
              </span>
            </div>

            <div className="space-y-6">
              <div className="p-5 bg-slate-50/50 dark:bg-white/[0.02] rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">
                      Average Score
                    </span>
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      vs. Team Average
                    </span>
                  </div>
                  <span
                    className={`text-sm font-bold ${
                      (stats?.averageScore ?? 0) >= (teamStats?.averageScore ?? 0)
                        ? 'text-emerald-500'
                        : 'text-error'
                    }`}
                  >
                    {(stats?.averageScore ?? 0) >= (teamStats?.averageScore ?? 0) ? '+' : ''}
                    {((stats?.averageScore ?? 0) - (teamStats?.averageScore ?? 0)).toFixed(1)}
                  </span>
                </div>
                <div className="relative h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-1000"
                    style={{ width: `${stats?.averageScore ?? 0}%` }}
                  />
                  <div
                    className="absolute inset-y-0 w-0.5 bg-white dark:bg-slate-900 z-10"
                    style={{ left: `${teamStats?.averageScore ?? 0}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    {isAdmin ? selectedMemberName.split(' ')[0] : 'You'}:{' '}
                    {(stats?.averageScore ?? 0).toFixed(1)}
                  </span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                    Team: {(teamStats?.averageScore ?? 0).toFixed(1)}
                  </span>
                </div>
              </div>

              <div className="p-5 bg-slate-50/50 dark:bg-white/[0.02] rounded-2xl border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest block">
                      Compliance Flags
                    </span>
                    <span className="text-sm font-bold text-slate-900 dark:text-white">
                      Flag Rate vs. Team
                    </span>
                  </div>
                  <span
                    className={`text-[10px] font-black uppercase tracking-widest ${
                      personalFlagPct === teamFlagPct
                        ? 'text-slate-400'
                        : personalFlagPct < teamFlagPct
                          ? 'text-emerald-500'
                          : 'text-error'
                    }`}
                  >
                    {personalFlagPct === teamFlagPct
                      ? 'On Par With Team'
                      : personalFlagPct < teamFlagPct
                        ? 'Fewer Flags Than Team'
                        : 'More Flags Than Team'}
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex-1 space-y-1">
                    <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest">
                      <span className="text-slate-400">{isAdmin ? 'Member' : 'Personal'} Flags</span>
                      <span className="text-slate-900 dark:text-white">
                        {stats?.flaggedVideoCount ?? 0}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-secondary rounded-full transition-all duration-1000"
                        style={{ width: `${personalFlagPct}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-slate-400 font-bold">
                      {personalFlagPct.toFixed(0)}% of scored
                    </span>
                  </div>
                  <div className="w-px h-10 bg-slate-100 dark:bg-slate-800 mx-2" />
                  <div className="flex-1 space-y-1">
                    <div className="flex justify-between text-[9px] font-bold uppercase tracking-widest">
                      <span className="text-slate-400">Team Avg</span>
                      <span className="text-slate-900 dark:text-white">
                        {teamStats?.flaggedVideoCount ?? 0}
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-slate-300 dark:bg-slate-700 rounded-full transition-all duration-1000"
                        style={{ width: `${teamFlagPct}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-slate-400 font-bold">
                      {teamFlagPct.toFixed(0)}% of scored
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Signal Breakdown */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-8 rounded-2xl shadow-sm mt-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 flex items-center justify-center text-emerald-500">
              <span className="material-symbols-outlined text-[24px]">neurology</span>
            </div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
              Signal Breakdown
            </h2>
          </div>
          {(!isAdmin || !!selectedUserId) && teamStats?.signalAverages && (
            <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-widest">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-1.5 rounded-full bg-primary inline-block" />{' '}
                {isAdmin ? selectedMemberName.split(' ')[0] : 'You'}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700 inline-block" /> Team
              </span>
            </div>
          )}
        </div>
        {stats?.signalAverages ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-5">
            {Object.entries(SIGNAL_LABELS).map(([key, label]) => {
              const avg = stats.signalAverages?.[key] ?? null;
              const teamAvg = teamStats?.signalAverages?.[key] ?? null;
              const delta = avg !== null && teamAvg !== null ? avg - teamAvg : null;
              const barColor =
                avg === null ? 'bg-slate-200 dark:bg-slate-800'
                  : avg >= 70 ? 'bg-emerald-500'
                    : avg >= 40 ? 'bg-primary'
                      : 'bg-red-500';
              const scoreColor =
                avg === null ? 'text-slate-400'
                  : avg >= 70 ? 'text-emerald-600 dark:text-emerald-400'
                    : avg >= 40 ? 'text-primary'
                      : 'text-red-500 dark:text-red-400';
              return (
                <div key={key}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                        {label}
                      </span>
                      {avg !== null && (
                        <span className={`text-[11px] font-black ${scoreColor}`}>{avg}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {delta !== null && (!isAdmin || !!selectedUserId) && (
                        <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-lg ${
                          delta >= 0
                            ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                            : 'text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/20'
                        }`}>
                          {delta >= 0 ? '+' : ''}{delta}
                        </span>
                      )}
                      <span className="text-[9px] font-black text-slate-300 dark:text-slate-700 tracking-wider">
                        {SIGNAL_WEIGHTS[key]}
                      </span>
                    </div>
                  </div>
                  <div className="relative h-2 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 ${barColor} rounded-full transition-all duration-1000 ease-out`}
                      style={{ width: `${avg ?? 0}%` }}
                    />
                    {teamAvg !== null && (!isAdmin || !!selectedUserId) && (
                      <div
                        className="absolute inset-y-0 w-0.5 bg-slate-500 dark:bg-slate-400 z-10"
                        style={{ left: `${teamAvg}%` }}
                      />
                    )}
                  </div>
                  {teamAvg !== null && (!isAdmin || !!selectedUserId) && (
                    <div className="mt-1">
                      <span className="text-[9px] font-bold text-slate-400 dark:text-slate-600">
                        Team avg: {teamAvg}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-slate-50 dark:bg-white/[0.02] rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
            <span className="material-symbols-outlined text-slate-400 text-[18px]">info</span>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
              Signal intelligence will be available once videos are scored
            </p>
          </div>
        )}
      </div>
    </>
  );
};

export default TeamPerformanceTab;
